/**
 * Popup Custom Module
 *
 * Handles configurable promotional popups that show on page load.
 * Supports multiple independently scheduled popup blocks.
 * - Queues popups to prevent concurrent show() calls
 * - Only shows after the location redirect choice has been confirmed
 * - Stores a configurable flag in localStorage so it only shows once per visitor
 * - Uses native <dialog> element for accessibility
 *
 * @module popup-custom
 */

import {
  SELECTORS,
  LOCATION_STORAGE_KEY,
  LOCATION_CHOICE_EVENT,
  DEFERRED_SHOW_DELAY,
} from './constants.js';
import { hasSeen, hasCountrySelected } from './storage.js';
import { openPopup, closePopupPreview, bindEvents } from './ui.js';
import { isWithinSchedule } from '../utils/scheduler.js?inline=2';

export { SELECTORS, CLASSES, LOCATION_STORAGE_KEY, LOCATION_CHOICE_EVENT } from './constants.js';
export { hasSeen, markSeen, hasCountrySelected } from './storage.js';
export { openPopup, closePopup, closePopupPreview, bindEvents } from './ui.js';

interface PopupConfig {
  blockId: string;
  storageKey: string;
  delay: number;
  startDatetime: string;
  endDatetime: string;
}

interface QueuedPopup {
  dialog: HTMLDialogElement;
  storageKey: string;
  delay: number;
}

const popupQueue: QueuedPopup[] = [];
let isShowing = false;
let editorCleanup: (() => void) | null = null;

/**
 * Processes the popup queue — shows one popup at a time
 */
function processQueue() {
  if (isShowing || popupQueue.length === 0) return;
  isShowing = true;
  const { dialog, storageKey, delay } = popupQueue.shift()!;
  setTimeout(() => openPopup(dialog, storageKey), delay);
  dialog.addEventListener(
    'close',
    () => {
      isShowing = false;
      processQueue();
    },
    { once: true },
  );
}

/**
 * Parses configuration from a given inline JSON script element
 * @param {HTMLElement} el - The script element containing JSON config
 * @returns {Object|null} Parsed config or null on failure
 */
function parseConfigFromElement(el: HTMLElement): PopupConfig | null {
  if (!el) return null;

  try {
    return JSON.parse(el.textContent!) as unknown as PopupConfig;
  } catch {
    return null;
  }
}

/**
 * Main initialization function — iterates over all popup block configs
 */
export function init() {
  popupQueue.length = 0;
  isShowing = false;

  const configElements = document.querySelectorAll<HTMLElement>(SELECTORS.configPrefix);
  if (!configElements.length) return;

  const forceKey = new URLSearchParams(window.location.search).get('debug_popup');

  configElements.forEach((configEl) => {
    const config = parseConfigFromElement(configEl);
    if (!config) return;

    const dialog = document.getElementById(
      'popup-custom-' + config.blockId,
    ) as HTMLDialogElement | null;
    if (!dialog) return;

    if (forceKey && forceKey === config.storageKey) {
      bindEvents(dialog);
      popupQueue.push({ dialog, storageKey: config.storageKey, delay: 0 });
      return;
    }

    if (Shopify!.designMode) {
      bindEvents(dialog, { preview: true });
      return;
    }

    if (hasSeen(config.storageKey)) return;

    const hasSchedule = !!config.startDatetime || !!config.endDatetime;
    if (hasSchedule && !isWithinSchedule(config.startDatetime, config.endDatetime)) return;

    if (!hasCountrySelected(LOCATION_STORAGE_KEY)) {
      document.addEventListener(
        LOCATION_CHOICE_EVENT,
        () => {
          setTimeout(() => {
            bindEvents(dialog);
            popupQueue.push({ dialog, storageKey: config.storageKey, delay: config.delay });
            processQueue();
          }, DEFERRED_SHOW_DELAY);
        },
        { once: true },
      );
      return;
    }

    bindEvents(dialog);
    popupQueue.push({ dialog, storageKey: config.storageKey, delay: config.delay });
  });

  if (Shopify!.designMode) {
    registerEditorEvents();
    return;
  }

  processQueue();
}

/**
 * Registers Shopify theme editor event listeners for design mode.
 * Shows/hides popups when their blocks are selected/deselected in the sidebar.
 */
function registerEditorEvents() {
  if (editorCleanup) editorCleanup();

  function onBlockSelect(event: Event) {
    const dialog = (event.target as Element).closest<HTMLDialogElement>(
      'dialog[id^="popup-custom-"]',
    );
    if (!dialog || dialog.open) return;
    openPopup(dialog);
  }

  function onBlockDeselect(event: Event) {
    const dialog = (event.target as Element).closest<HTMLDialogElement>(
      'dialog[id^="popup-custom-"]',
    );
    if (!dialog || !dialog.open) return;
    closePopupPreview(dialog);
  }

  function onSectionLoad(event: Event) {
    if (!(event.target as Element).querySelector(SELECTORS.configPrefix)) return;
    init();
  }

  document.addEventListener('shopify:block:select', onBlockSelect);
  document.addEventListener('shopify:block:deselect', onBlockDeselect);
  document.addEventListener('shopify:section:load', onSectionLoad);

  editorCleanup = () => {
    document.removeEventListener('shopify:block:select', onBlockSelect);
    document.removeEventListener('shopify:block:deselect', onBlockDeselect);
    document.removeEventListener('shopify:section:load', onSectionLoad);
    editorCleanup = null;
  };
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
